import { API_CONFIG, RUNTIME_CONFIG } from '../config';
import apiClient from './apiClient';

const DAY_LABEL_FORMATTER = new Intl.DateTimeFormat('en-IN', {
  weekday: 'short',
  day: '2-digit',
  month: 'short'
});

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat('en-IN', {
  month: 'long',
  year: 'numeric'
});

const normalizeMonth = (monthIndex) => {
  return Number(monthIndex) + 1;
};

const toIsoDate = (value) => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const year = parsed.getFullYear();
  const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsed.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDayLabel = (isoDate) => {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return String(isoDate || '-');
  }
  return DAY_LABEL_FORMATTER.format(parsed);
};

const normalizeType = (rawType = '') => {
  const normalized = String(rawType).toLowerCase().replace(/\s+/g, '_');

  if (normalized.includes('ekadashi')) return 'ekadashi';
  if (normalized.includes('purnima') || normalized.includes('poornima')) return 'purnima';
  if (normalized.includes('amavasya')) return 'amavasya';
  if (normalized.includes('festival')) return 'festival';
  return 'auspicious';
};

const normalizeEntries = (entries = []) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((entry, index) => {
      const rawDate = entry?.date || entry?.isoDate || entry?.day || entry?.timestamp;
      const isoDate = toIsoDate(rawDate);
      if (!isoDate) {
        return null;
      }

      return {
        id: entry?.id || `${isoDate}-${index}`,
        date: isoDate,
        dayLabel: entry?.dayLabel || formatDayLabel(isoDate),
        title: entry?.title || entry?.name || 'Important Panchang Date',
        type: normalizeType(entry?.type || entry?.category || entry?.eventType),
        note: entry?.note || entry?.description || ''
      };
    })
    .filter(Boolean)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
};

const normalizePayload = ({ payload, year, monthIndex, city }) => {
  const data = payload?.data || payload || {};
  const entries = normalizeEntries(
    data?.entries ||
    data?.highlights ||
    data?.events ||
    data?.days ||
    []
  );

  const monthDate = new Date(year, monthIndex, 1);
  const monthLabel = data?.monthLabel || MONTH_LABEL_FORMATTER.format(monthDate);
  const todayIso = toIsoDate(new Date());

  const today = entries.find((entry) => entry.date === todayIso) || entries[0] || null;

  return {
    monthKey: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
    monthLabel,
    city: data?.city || city || 'Location not selected',
    today,
    entries
  };
};

const getInternalApiHighlights = async ({ year, monthIndex, city }) => {
  const month = normalizeMonth(monthIndex);
  const query = new URLSearchParams({
    year: String(year),
    month: String(month)
  });

  if (city) {
    query.set('city', city);
  }

  const endpoint = API_CONFIG.ENDPOINTS.PANCHANG?.HIGHLIGHTS || RUNTIME_CONFIG.PANCHANG_API_PATH;
  const response = await apiClient.get(`${endpoint}?${query.toString()}`);
  return normalizePayload({ payload: response, year, monthIndex, city });
};

const getExternalProviderHighlights = async ({ year, monthIndex, city }) => {
  const providerUrl = String(RUNTIME_CONFIG.PANCHANG_PROVIDER_URL || '').trim();
  if (!providerUrl) {
    throw new Error('Panchang provider URL is not configured');
  }

  const month = normalizeMonth(monthIndex);
  const query = new URLSearchParams({
    year: String(year),
    month: String(month)
  });
  if (city) {
    query.set('city', city);
  }

  const url = providerUrl.includes('?')
    ? `${providerUrl}&${query.toString()}`
    : `${providerUrl}?${query.toString()}`;

  const headers = {
    'Content-Type': 'application/json'
  };

  if (RUNTIME_CONFIG.PANCHANG_API_KEY) {
    headers.Authorization = `Bearer ${RUNTIME_CONFIG.PANCHANG_API_KEY}`;
    headers['x-api-key'] = RUNTIME_CONFIG.PANCHANG_API_KEY;
  }

  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) {
    throw new Error(`Panchang provider request failed (${response.status})`);
  }

  const payload = await response.json();
  return normalizePayload({ payload, year, monthIndex, city });
};

const getMonthlyHighlights = async ({ year, monthIndex, city }) => {
  if (RUNTIME_CONFIG.PANCHANG_PROVIDER_URL) {
    return getExternalProviderHighlights({ year, monthIndex, city });
  }

  return getInternalApiHighlights({ year, monthIndex, city });
};

export const panchangService = {
  getMonthlyHighlights
};

export default panchangService;
